import { defaultQueryOptions } from 'constants/'
import { useItemService } from 'hooks/services/useItemService'
import { useQuery } from 'react-query'
import { QueryOptions, DeviceCouplingLeftAndRight, DeviceCouplingSlotType, DeviceCouplingDTO, AccountType } from 'types'
import { logFactory } from 'utils'
import { useDeviceService, GetCouplingQuery } from '../services'
import { transformCouplingInfoFromDeviceService } from './transformCoupling/index'

interface ResponseStatusContext {
  isError: boolean
  isLoading: boolean
  refetch: () => void
  isRefetching: boolean
}
type SingleDeviceCoupling = DeviceCouplingLeftAndRight &
  DeviceCouplingDTO & {
    isLatestAppointmentsCoupling?: boolean
  }

interface CouplingContextFromDeviceService extends ResponseStatusContext {
  data?: { latestCoupling?: SingleDeviceCoupling; couplings: SingleDeviceCoupling[] }
}

const deviceCouplingHookName = 'useDeviceCoupling'
const itemServiceHookName = 'useItemService'
const logDeviceCoupling = logFactory(deviceCouplingHookName)
const logItemService = logFactory(itemServiceHookName)

const useCouplingFromDeviceService = (
  customerGid: string,
  opportunityGid: string,
  options: QueryOptions = { enabled: true },
  createdByType?: AccountType
): CouplingContextFromDeviceService => {
  const { getLatestCoupling } = useDeviceService()
  const { getItems } = useItemService()

  let latestCouplingParams = {
    customerSalesforceId: customerGid,
    opportunitySalesforceId: opportunityGid,
  } as GetCouplingQuery

  if (createdByType) {
    latestCouplingParams = { ...latestCouplingParams, createdBy: createdByType }
  }

  const fetchCoupling = async () => {
    logDeviceCoupling(`🦻 Fetching coupling...`, latestCouplingParams)
    const response = await getLatestCoupling(latestCouplingParams)

    logDeviceCoupling(`🦻 Found ${response.data.length} coupling(s)`)

    if (!response.data.length) {
      return {
        couplings: [],
        hasCoupling: false,
        latestCoupling: undefined,
      }
    }

    const couplings = []

    for (let index = 0; index < response.data.length; index++) {
      let coupling = transformCouplingInfoFromDeviceService(response.data[index])

      if (coupling.hasCoupling) {
        const [leftReceiversInDeviceCoupling, rightReceiversInDeviceCoupling] = [
          coupling.left?.slots.find((sl) => sl.type === DeviceCouplingSlotType.RECEIVER_TUBE),
          coupling.right?.slots.find((sl) => sl.type === DeviceCouplingSlotType.RECEIVER_TUBE),
        ]

        const availableReceiverGids: string[] = []

        if (leftReceiversInDeviceCoupling) {
          availableReceiverGids.push(leftReceiversInDeviceCoupling.itemGid)
        }
        if (rightReceiversInDeviceCoupling) {
          availableReceiverGids.push(rightReceiversInDeviceCoupling.itemGid)
        }

        logItemService(`🦻 Fetching receiver details...`, availableReceiverGids)
        const itemServiceResponse = await getItems({ gids: availableReceiverGids })

        logItemService(`🦻 Found ${itemServiceResponse.data.length} receiver item(s)`)

        if (leftReceiversInDeviceCoupling && coupling.left) {
          coupling = {
            ...coupling,
            left: {
              ...coupling.left,
              slots: coupling.left.slots.map((sl) => {
                if (sl.customerItemId === leftReceiversInDeviceCoupling.customerItemId) {
                  const matchingItemFromResponse = itemServiceResponse.data.find(
                    (item) => item.gid === leftReceiversInDeviceCoupling.itemGid
                  )

                  return {
                    ...sl,
                    length: matchingItemFromResponse && matchingItemFromResponse.length,
                    strength: matchingItemFromResponse && matchingItemFromResponse.strength,
                  }
                }
                return sl
              }),
            },
          }
        }

        if (rightReceiversInDeviceCoupling && coupling.right) {
          coupling = {
            ...coupling,
            right: {
              ...coupling.right,
              slots: coupling.right.slots.map((sl) => {
                if (sl.customerItemId === rightReceiversInDeviceCoupling.customerItemId) {
                  const matchingItemFromResponse = itemServiceResponse.data.find(
                    (item) => item.gid === rightReceiversInDeviceCoupling.itemGid
                  )

                  return {
                    ...sl,
                    length: matchingItemFromResponse && matchingItemFromResponse.length,
                    strength: matchingItemFromResponse && matchingItemFromResponse.strength,
                  }
                }
                return sl
              }),
            },
          }
        }
        couplings.push(coupling)
      }
    }

    return { couplings, latestCoupling: couplings[0] }
  }

  return useQuery([deviceCouplingHookName, { customerGid, opportunityGid }], fetchCoupling, {
    ...defaultQueryOptions,
    ...options,
  })
}

export type { SingleDeviceCoupling }
export { useCouplingFromDeviceService }
